import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '../../../../shared/hooks/useDebounce';
import { organizationApi } from '../services/contactsApi';
import type { OrganizationCreate, OrganizationUpdate, ListOrganizationsParams } from '../types';

export const organizationKeys = {
  all: ['organizations'] as const,
  list: (params?: ListOrganizationsParams) => [...organizationKeys.all, 'list', params] as const,
  detail: (id: string) => [...organizationKeys.all, 'detail', id] as const,
  domainMatch: (domain: string) => [...organizationKeys.all, 'domain-match', domain] as const,
};

/**
 * Extract a bare host from a contact email (e.g. `fred@Acme.com` → `acme.com`).
 * Returns '' for personal/free-mail providers and anything not domain-shaped,
 * so we never suggest an organization for gmail.com / outlook.com etc.
 */
// Intentionally non-exhaustive: the common free-mail providers worth filtering
// so we never suggest an organization for a personal address. A miss here only
// costs one no-op match-domain lookup that returns nothing.
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'yahoo.com', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'proton.me', 'protonmail.com', 'gmx.com', 'mail.com', 'zoho.com', 'msn.com',
]);

export function domainFromEmail(email: string | undefined | null): string {
  if (!email) return '';
  const at = email.indexOf('@');
  if (at === -1) return '';
  const host = email.slice(at + 1).trim().toLowerCase();
  // Require a domain-shaped host with a TLD before we bother the backend.
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(host)) return '';
  if (FREE_EMAIL_DOMAINS.has(host)) return '';
  return host;
}

/**
 * Suggest an existing organization matching the domain of a contact's email.
 * Debounced; only queries once the email contains a real company domain.
 */
export function useOrganizationDomainMatch(email: string | undefined, enabled = true) {
  const domain = domainFromEmail(email);
  const debouncedDomain = useDebounce(domain, 400);
  const query = useQuery({
    queryKey: organizationKeys.domainMatch(debouncedDomain),
    queryFn: () => organizationApi.matchByDomain(debouncedDomain),
    enabled: enabled && debouncedDomain.length > 0,
    staleTime: 60_000,
  });
  return { domain: debouncedDomain, match: query.data ?? null, isFetching: query.isFetching };
}

/**
 * Clean undefined values from params for consistent query key comparison.
 */
function cleanParams(params?: ListOrganizationsParams): ListOrganizationsParams | undefined {
  if (!params) return undefined;
  const cleaned = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined)
  ) as ListOrganizationsParams;
  return Object.keys(cleaned).length > 0 ? cleaned : undefined;
}

export function useOrganizations(params?: ListOrganizationsParams) {
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only recompute when specific fields change
  const stableParams = useMemo(() => cleanParams(params), [
    params?.search,
    params?.industry,
    params?.tier_id,
    params?.owner_id,
    params?.organization_relationship,
    params?.tags,
    params?.include_archived,
    params?.custom_fields,
    params?.limit,
    params?.offset,
    params?.admin_scope,
  ]);

  return useQuery({
    queryKey: organizationKeys.list(stableParams),
    queryFn: () => organizationApi.list(stableParams),
  });
}

export function useOrganization(id: string) {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: () => organizationApi.get(id),
    enabled: !!id,
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OrganizationCreate) => organizationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: OrganizationUpdate }) => organizationApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useDeleteOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useArchiveOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useUnarchiveOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationApi.unarchive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useBulkDeleteOrganizations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => organizationApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}

export function useCascadeRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      organizationId,
      contactRelationship,
      contactIds,
    }: {
      organizationId: string;
      contactRelationship: string[];
      contactIds?: string[];
    }) => organizationApi.cascadeRelationship(organizationId, contactRelationship, contactIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useBulkArchiveOrganizations() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => organizationApi.bulkArchive(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.all });
    },
  });
}
